import React, { useState, useCallback, useEffect } from "react";
import { Form } from "react-bootstrap";
import {
  Plus, Pencil, Trash2, Map, ChevronRight, Eye,
} from "lucide-react";
import * as plmSvc from "../../services/plmService";
import EntityFormDialog from "./EntityFormDialog";
import TagInput from "./TagInput";
import JourneyGrid from "./JourneyGrid";

const EMPTY_MAP = { title: "", description: "", personaId: "", tags: [] };
const EMPTY_STEP = {
  stepOrder: 1, phaseName: "", userAction: "", thoughtsFeelings: "", touchpoints: "",
  emotion: "", sentimentScore: "", effortScore: "", painPoint: "", opportunity: "", channel: "",
};

const JourneysTab = ({ token, plm }) => {
  const {
    journeyMaps, personas, tags: allTags,
    createJourneyMap, updateJourneyMap, deleteJourneyMap,
  } = plm;

  const tagNames = allTags.map((t) => t.name);

  // Journey map dialog
  const [showMapDlg, setShowMapDlg] = useState(false);
  const [editingMap, setEditingMap] = useState(null);
  const [mapForm, setMapForm] = useState(EMPTY_MAP);

  // Step dialog
  const [showStepDlg, setShowStepDlg] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [stepForm, setStepForm] = useState(EMPTY_STEP);
  const [stepMapId, setStepMapId] = useState(null);

  // Active journey (expanded to grid view)
  const [activeJourneyId, setActiveJourneyId] = useState(null);
  const [steps, setSteps] = useState([]);
  const [loadingSteps, setLoadingSteps] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadDetail = useCallback(async (mapId) => {
    setLoadingSteps(true);
    try {
      const detail = await plmSvc.fetchJourneyMapByIdApi({ token, journeyMapId: mapId });
      setSteps(detail.steps || []);
    } catch { setSteps([]); }
    finally { setLoadingSteps(false); }
  }, [token]);

  useEffect(() => {
    if (activeJourneyId) loadDetail(activeJourneyId);
  }, [activeJourneyId, loadDetail]);

  // Map CRUD
  const openCreateMap = () => { setEditingMap(null); setMapForm(EMPTY_MAP); setShowMapDlg(true); };
  const openEditMap = (m) => {
    setEditingMap(m);
    setMapForm({ title: m.title, description: m.description || "", personaId: m.personaId || "", tags: m.tags || [] });
    setShowMapDlg(true);
  };

  const handleMapSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { ...mapForm };
    if (!body.personaId) delete body.personaId;
    if (editingMap) {
      await updateJourneyMap(editingMap.id, body);
    } else {
      await createJourneyMap(body);
    }
    setSaving(false);
    setShowMapDlg(false);
  };

  const handleDeleteMap = async (id) => {
    if (!window.confirm("Delete this journey map and all its steps?")) return;
    await deleteJourneyMap(id);
    if (activeJourneyId === id) setActiveJourneyId(null);
  };

  // Step CRUD
  const openCreateStep = (mapId) => {
    setStepMapId(mapId);
    setEditingStep(null);
    setStepForm({ ...EMPTY_STEP, stepOrder: steps.length + 1 });
    setShowStepDlg(true);
  };

  const openEditStep = (mapId, step) => {
    setStepMapId(mapId);
    setEditingStep(step);
    setStepForm({
      stepOrder: step.stepOrder,
      phaseName: step.phaseName || "",
      userAction: step.userAction || "",
      thoughtsFeelings: step.thoughtsFeelings || "",
      touchpoints: step.touchpoints || "",
      emotion: step.emotion || "",
      sentimentScore: step.sentimentScore != null ? step.sentimentScore : "",
      effortScore: step.effortScore != null ? step.effortScore : "",
      painPoint: step.painPoint || "",
      opportunity: step.opportunity || "",
      channel: step.channel || "",
    });
    setShowStepDlg(true);
  };

  const handleStepSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { ...stepForm };
    // Convert score strings to numbers or null
    body.sentimentScore = body.sentimentScore !== "" ? parseFloat(body.sentimentScore) : null;
    body.effortScore = body.effortScore !== "" ? parseFloat(body.effortScore) : null;
    if (editingStep) {
      await plmSvc.updateJourneyStepApi({ token, journeyMapId: stepMapId, stepId: editingStep.id, step: body });
    } else {
      await plmSvc.createJourneyStepApi({ token, journeyMapId: stepMapId, step: body });
    }
    await loadDetail(stepMapId);
    setSaving(false);
    setShowStepDlg(false);
  };

  const handleDeleteStep = async (mapId, stepId) => {
    if (!window.confirm("Delete this step?")) return;
    await plmSvc.deleteJourneyStepApi({ token, journeyMapId: mapId, stepId });
    await loadDetail(mapId);
  };

  const setMap = (k, v) => setMapForm({ ...mapForm, [k]: v });
  const setStep = (k, v) => setStepForm({ ...stepForm, [k]: v });

  const activeJourney = journeyMaps.find(j => j.id === activeJourneyId);

  return (
    <div>
      {/* Header */}
      <div className="plm-section-header">
        <h2 className="plm-section-title">
          <span className="icon-wrap"><Map size={20} /></span>
          {activeJourney ? (
            <span className="d-flex align-items-center gap-2">
              <button
                className="plm-btn-ghost"
                style={{ padding: "4px 8px" }}
                onClick={() => setActiveJourneyId(null)}
              >
                All Journeys
              </button>
              <ChevronRight size={14} style={{ color: "var(--plm-text-muted)" }} />
              {activeJourney.title}
              {activeJourney.personaName && (
                <span className="plm-badge purple">{activeJourney.personaName}</span>
              )}
            </span>
          ) : (
            "Journey Maps"
          )}
        </h2>
        <div className="d-flex gap-2">
          {activeJourney && (
            <>
              <button className="plm-btn-ghost" onClick={() => openEditMap(activeJourney)}>
                <Pencil size={14} /> Edit
              </button>
              <button className="plm-btn-ghost danger" onClick={() => handleDeleteMap(activeJourney.id)}>
                <Trash2 size={14} />
              </button>
            </>
          )}
          <button className="plm-btn-primary" onClick={openCreateMap}>
            <Plus size={16} /> New Journey
          </button>
        </div>
      </div>

      {/* Journey List or Grid View */}
      {!activeJourneyId ? (
        // --- List View ---
        journeyMaps.length === 0 ? (
          <div className="plm-empty">
            <div className="plm-empty-icon"><Map size={28} /></div>
            <div className="plm-empty-title">No journey maps yet</div>
            <div className="plm-empty-desc">Map out your first customer journey to visualize the experience.</div>
            <button className="plm-btn-primary" onClick={openCreateMap}>
              <Plus size={16} /> Create Journey
            </button>
          </div>
        ) : (
          <div className="plm-card">
            {journeyMaps.map((m) => (
              <div
                key={m.id}
                className="plm-journey-list-item"
                onClick={() => setActiveJourneyId(m.id)}
              >
                <div className="d-flex align-items-center gap-3">
                  <div style={{
                    width: 40, height: 40, borderRadius: 10,
                    background: "var(--plm-primary-light)", color: "var(--plm-primary)",
                    display: "flex", alignItems: "center", justifyContent: "center"
                  }}>
                    <Map size={18} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: "0.95rem" }}>{m.title}</div>
                    <div className="d-flex align-items-center gap-2" style={{ marginTop: 2 }}>
                      {m.personaName && <span className="plm-badge purple">{m.personaName}</span>}
                      {m.steps && <span className="plm-badge gray">{m.steps.length} steps</span>}
                      {m.tags?.map((t) => <span key={t} className="plm-tag">{t}</span>)}
                    </div>
                    {m.description && (
                      <div style={{ fontSize: "0.8rem", color: "var(--plm-text-muted)", marginTop: 2 }}>
                        {m.description.length > 100 ? m.description.slice(0, 100) + "..." : m.description}
                      </div>
                    )}
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <button className="plm-btn-ghost" onClick={() => setActiveJourneyId(m.id)}>
                    <Eye size={14} /> View
                  </button>
                  <button className="plm-btn-ghost" onClick={() => openEditMap(m)}>
                    <Pencil size={14} />
                  </button>
                  <button className="plm-btn-ghost danger" onClick={() => handleDeleteMap(m.id)}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        // --- Grid View ---
        loadingSteps ? (
          <div className="plm-loading">
            <div className="plm-loading-dots"><span /><span /><span /></div>
          </div>
        ) : (
          <JourneyGrid
            steps={steps}
            journeyMapId={activeJourneyId}
            onEditStep={openEditStep}
            onDeleteStep={handleDeleteStep}
            onCreateStep={openCreateStep}
          />
        )
      )}

      {/* Journey Map Form */}
      <EntityFormDialog show={showMapDlg} onClose={() => setShowMapDlg(false)}
        title={editingMap ? "Edit Journey Map" : "New Journey Map"} onSubmit={handleMapSubmit} saving={saving}>
        <Form.Group className="mb-3">
          <Form.Label>Title *</Form.Label>
          <Form.Control required value={mapForm.title} onChange={(e) => setMap("title", e.target.value)} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Persona</Form.Label>
          <Form.Select value={mapForm.personaId} onChange={(e) => setMap("personaId", e.target.value)}>
            <option value="">-- None --</option>
            {personas.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Description</Form.Label>
          <Form.Control as="textarea" rows={2} value={mapForm.description} onChange={(e) => setMap("description", e.target.value)} />
        </Form.Group>
        <Form.Group>
          <Form.Label>Tags</Form.Label>
          <TagInput tags={mapForm.tags} onChange={(t) => setMap("tags", t)} suggestions={tagNames} />
        </Form.Group>
      </EntityFormDialog>

      {/* Step Form — Rich fields matching the new data model */}
      <EntityFormDialog show={showStepDlg} onClose={() => setShowStepDlg(false)}
        title={editingStep ? "Edit Phase / Step" : "New Phase / Step"} onSubmit={handleStepSubmit} saving={saving}>
        <div className="row">
          <Form.Group className="col-3 mb-3">
            <Form.Label>Order *</Form.Label>
            <Form.Control type="number" min={1} required value={stepForm.stepOrder}
              onChange={(e) => setStep("stepOrder", parseInt(e.target.value) || 1)} />
          </Form.Group>
          <Form.Group className="col-5 mb-3">
            <Form.Label>Phase / Stage Name *</Form.Label>
            <Form.Control required value={stepForm.phaseName} onChange={(e) => setStep("phaseName", e.target.value)}
              placeholder="e.g. Discover, Evaluate" />
          </Form.Group>
          <Form.Group className="col-4 mb-3">
            <Form.Label>Channel</Form.Label>
            <Form.Control value={stepForm.channel} onChange={(e) => setStep("channel", e.target.value)}
              placeholder="e.g. Web, Mobile, Email" />
          </Form.Group>
        </div>

        <Form.Group className="mb-3">
          <Form.Label>Touchpoint</Form.Label>
          <Form.Control value={stepForm.touchpoints} onChange={(e) => setStep("touchpoints", e.target.value)}
            placeholder="Where interaction happens (e.g. Landing Page, Chat)" />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>User Action</Form.Label>
          <Form.Control value={stepForm.userAction} onChange={(e) => setStep("userAction", e.target.value)}
            placeholder="What the user does (e.g. Reads product description)" />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Thoughts & Feelings</Form.Label>
          <Form.Control as="textarea" rows={2} value={stepForm.thoughtsFeelings}
            onChange={(e) => setStep("thoughtsFeelings", e.target.value)}
            placeholder="What the user thinks or feels at this point" />
        </Form.Group>

        <div style={{ borderTop: "1px solid #f3f4f6", margin: "8px 0 12px", paddingTop: 12 }}>
          <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "var(--plm-text-muted)", marginBottom: 8 }}>
            Experience & Signals
          </div>
        </div>

        <div className="row">
          <Form.Group className="col-4 mb-3">
            <Form.Label>Emotion</Form.Label>
            <Form.Control value={stepForm.emotion} onChange={(e) => setStep("emotion", e.target.value)}
              placeholder="e.g. Confused, Happy" />
          </Form.Group>
          <Form.Group className="col-4 mb-3">
            <Form.Label>Sentiment (-1 to 1)</Form.Label>
            <Form.Control type="number" step="0.1" min={-1} max={1}
              value={stepForm.sentimentScore}
              onChange={(e) => setStep("sentimentScore", e.target.value)}
              placeholder="-1.0 to 1.0" />
          </Form.Group>
          <Form.Group className="col-4 mb-3">
            <Form.Label>Effort (0 to 1)</Form.Label>
            <Form.Control type="number" step="0.1" min={0} max={1}
              value={stepForm.effortScore}
              onChange={(e) => setStep("effortScore", e.target.value)}
              placeholder="0.0 to 1.0" />
          </Form.Group>
        </div>

        <div style={{ borderTop: "1px solid #f3f4f6", margin: "8px 0 12px", paddingTop: 12 }}>
          <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "var(--plm-text-muted)", marginBottom: 8 }}>
            Pain Points & Opportunities
          </div>
        </div>

        <Form.Group className="mb-3">
          <Form.Label>Pain Point</Form.Label>
          <Form.Control as="textarea" rows={2} value={stepForm.painPoint}
            onChange={(e) => setStep("painPoint", e.target.value)}
            placeholder="What's the problem at this step?" />
        </Form.Group>

        <Form.Group>
          <Form.Label>Opportunity</Form.Label>
          <Form.Control as="textarea" rows={2} value={stepForm.opportunity}
            onChange={(e) => setStep("opportunity", e.target.value)}
            placeholder="What could be improved?" />
        </Form.Group>
      </EntityFormDialog>
    </div>
  );
};

export default JourneysTab;
